import {Show} from 'platform/foundation';

import {useRef, type RefObject} from 'react';

import {ColumnMenuContext, type ColumnMenuContextValue} from '../../context/ColumnMenuContext';
import type {ActionsAlignment} from '../../types/ActionsAlignment';
import type {EagColumn} from '../../types/EagColumn';
import type {EagColumnState} from '../../types/EagColumnState';
import type {RowData} from '../../types/RowData';
import type {GroupRowModel} from '../../utils/groupRowModel';
import {GridBody} from '../GridBody/GridBody';
import {HeaderRow} from '../HeaderRow/HeaderRow';
import {LoadingTableSkeleton} from '../LoadingTableSkeleton/LoadingTableSkeleton';
import {SummaryRow} from '../SummaryRow/SummaryRow';
import {FloatingHorizontalScrollbar} from './FloatingHorizontalScrollbar';
import {TableColgroup} from './TableColgroup';

export type TableScrollAreaProps = {
  // Layout flags
  autoHeight?: boolean;
  isColumnsReady: boolean;
  gridCode: string;
  hasCheckboxColumn: boolean;
  actionColumnWidth: number;
  rowSelectMode: 'SINGLE' | 'MULTIPLE' | 'NONE';
  itemsPerPage: number;
  // Pre-resolved px values (LineHeightToPx[gridSettings.headerHeight / rowHeight])
  headerHeightPx: number | undefined;
  rowHeightPx: number;
  stripedRows?: boolean;
  disableAllActions?: boolean;
  actionsAlignment?: ActionsAlignment | null;
  isLoading: boolean;
  /** When true, keep previous rows visible during a refetch instead of showing skeletons. */
  hasSilentRefetching: boolean;
  isMobile?: boolean;
  // Refs
  tableRef: RefObject<HTMLTableElement | null>;
  colgroupRef: RefObject<HTMLTableColElement | null>;
  // Data
  rows: RowData[];
  previousRows: RowData[];
  selectedRowIds: Set<string>;
  /** Live-order group row model — null when the group header row is not shown. */
  groupRowModel: GroupRowModel | null;
  // Context value
  columnMenuContextValue: ColumnMenuContextValue;
  // Layout slices
  visibleColumns: EagColumn[];
  columnStateMap: Map<string, EagColumnState>;
  nonRightPinnedColumns: EagColumn[];
  rightPinnedColumns: EagColumn[];
  nonRightPinnedColumnStates: EagColumnState[];
  rightPinnedColumnStates: EagColumnState[];
  orderedVisibleColumnStates: EagColumnState[];
  visibleColumnIndexMap: Map<string, number>;
  colIndexMap: Map<string, number>;
  pinnedLeftOffsets: Map<string, number>;
  pinnedRightOffsets: Map<string, number>;
  lastPinnedLeftKey: string | null;
  firstPinnedRightKey: string | null;
  sortableIds: string[];
  // Summary row
  showSummaryRow: boolean;
  // Handlers
  onSort: (key: string, additive: boolean) => void;
  onResizeStart: (
    colKey: string,
    startX: number,
    colIndex: number,
    resizeFromLeft?: boolean
  ) => void;
  onRowClick?: (row: RowData, event: React.MouseEvent) => void;
  onRowContextMenu?: (row: RowData, event: React.MouseEvent) => void;
  onActionClick?: (actionKey: string, row: RowData, event: React.MouseEvent) => void;
  onToggleSelection?: (rowId: string, extendRange?: boolean) => void;
};

// Styled scrollbars (T20-139328): with native macOS OVERLAY scrollbars the grid's
// positioned content — sticky pinned columns, `relative` cells, absolute resize
// handles — wins Chromium's hit test over the scrollbar drawn on top of it, so
// grabbing a thumb hits the content and the drag never scrolls. Styling
// `::-webkit-scrollbar` switches Chromium/Safari to classic scrollbars that occupy
// a layout gutter OUTSIDE the content box, where nothing can cover them (Firefox
// ignores the pseudo-elements and keeps its native scrollbars).
const SCROLLBAR_CLASSES =
  '[&::-webkit-scrollbar]:w-2.5 [&::-webkit-scrollbar]:h-2.5' +
  ' [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300' +
  ' [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-solid' +
  ' [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-clip-padding' +
  ' [&::-webkit-scrollbar-thumb:hover]:bg-slate-400' +
  ' [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-corner]:bg-transparent';

// Extracted to avoid duplicating the scroll container + table structure between the
// DndContext (columnMovingAllowed=true) and plain-div (columnMovingAllowed=false) branches.
// The only difference between those branches is the outer DndContext wrapper and the DragOverlay.
export function TableScrollArea(props: TableScrollAreaProps) {
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div
        ref={scrollAreaRef}
        data-eag-grid-scroll-area
        data-auto-height={props.autoHeight === true}
        className={
          props.autoHeight
            ? `overflow-x-auto overflow-y-visible w-full min-w-0 ${SCROLLBAR_CLASSES}`
            : `flex-1 overflow-auto w-full min-w-0 ${SCROLLBAR_CLASSES}`
        }
      >
        {/* isColumnsReady is false until applyPresetColumnsAndSort has run once.
            Before that, column widths are defaults — rendering the header and
            column-aware skeleton would cause a visible width-jump flash.
            Instead we show a plain full-width skeleton with no column structure. */}
        {props.isColumnsReady ? (
          <table
            ref={props.tableRef}
            className="w-full text-[13px] table-fixed"
            style={{borderCollapse: 'separate', borderSpacing: 0}}
            aria-label={props.gridCode}
          >
            <TableColgroup
              colgroupRef={props.colgroupRef}
              hasCheckboxColumn={props.hasCheckboxColumn}
              nonRightPinnedColumnStates={props.nonRightPinnedColumnStates}
              rightPinnedColumnStates={props.rightPinnedColumnStates}
              actionColumnWidth={props.actionColumnWidth}
            />
            <ColumnMenuContext.Provider value={props.columnMenuContextValue}>
              <HeaderRow
                columns={props.visibleColumns}
                columnStateMap={props.columnStateMap}
                nonRightPinnedColumns={props.nonRightPinnedColumns}
                rightPinnedColumns={props.rightPinnedColumns}
                onSort={props.onSort}
                onResizeStart={props.onResizeStart}
                sortableIds={props.sortableIds}
                pinnedLeftOffsets={props.pinnedLeftOffsets}
                pinnedRightOffsets={props.pinnedRightOffsets}
                groupRowModel={props.groupRowModel}
                lastPinnedLeftKey={props.lastPinnedLeftKey}
                firstPinnedRightKey={props.firstPinnedRightKey}
                actionColumnWidth={
                  props.actionColumnWidth > 0 ? props.actionColumnWidth : undefined
                }
                headerHeightPx={props.headerHeightPx}
                colIndexMap={props.colIndexMap}
                rowSelectMode={props.rowSelectMode}
              />
            </ColumnMenuContext.Provider>
            {/* drag state (activeDragId, overColumnId, dropSide) is read from store by leaf components */}
            <GridBody
              columnStates={props.orderedVisibleColumnStates}
              columnStateMap={props.columnStateMap}
              nonRightPinnedColumns={props.nonRightPinnedColumns}
              rightPinnedColumns={props.rightPinnedColumns}
              visibleColumnIndexMap={props.visibleColumnIndexMap}
              rows={props.rows}
              previousRows={props.previousRows}
              skeletonRowCount={props.itemsPerPage}
              isLoading={props.isLoading}
              hasSilentRefetching={props.hasSilentRefetching}
              selectedRowIds={props.selectedRowIds}
              onRowClick={props.onRowClick}
              pinnedLeftOffsets={props.pinnedLeftOffsets}
              pinnedRightOffsets={props.pinnedRightOffsets}
              stripedRows={props.stripedRows}
              lastPinnedLeftKey={props.lastPinnedLeftKey}
              firstPinnedRightKey={props.firstPinnedRightKey}
              onActionClick={props.actionColumnWidth > 0 ? props.onActionClick : undefined}
              onRowContextMenu={props.onRowContextMenu}
              actionColumnWidth={props.actionColumnWidth > 0 ? props.actionColumnWidth : undefined}
              isMobile={props.isMobile}
              disableAllActions={props.disableAllActions}
              actionsAlignment={props.actionsAlignment}
              rowSelectMode={props.rowSelectMode}
              onToggleSelection={props.onToggleSelection}
            />
            {/* drag state (activeDragId, overColumnId, dropSide) is read from store by GridCell/SkeletonRow */}
            {/* SummaryRow internally returns null when summaryRowCells hasn't fetched yet */}
            <Show when={props.showSummaryRow}>
              <SummaryRow
                columnStateMap={props.columnStateMap}
                nonRightPinnedColumns={props.nonRightPinnedColumns}
                rightPinnedColumns={props.rightPinnedColumns}
                visibleColumnIndexMap={props.visibleColumnIndexMap}
                pinnedLeftOffsets={props.pinnedLeftOffsets}
                pinnedRightOffsets={props.pinnedRightOffsets}
                lastPinnedLeftKey={props.lastPinnedLeftKey}
                firstPinnedRightKey={props.firstPinnedRightKey}
                actionColumnWidth={
                  props.actionColumnWidth > 0 ? props.actionColumnWidth : undefined
                }
                hasCheckboxColumn={props.hasCheckboxColumn}
              />
            </Show>
          </table>
        ) : (
          <LoadingTableSkeleton rowCount={props.itemsPerPage} rowHeightPx={props.rowHeightPx} />
        )}
        <div ref={bottomSentinelRef} className="h-px w-px -mt-px" aria-hidden />
      </div>
      <Show when={props.autoHeight}>
        <FloatingHorizontalScrollbar
          bottomSentinelRef={bottomSentinelRef}
          isColumnsReady={props.isColumnsReady}
          scrollAreaRef={scrollAreaRef}
        />
      </Show>
    </>
  );
}
